import { useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BsPlusCircle } from 'react-icons/bs'
import { FaRegTimesCircle } from 'react-icons/fa'
import { FaRegCircleCheck } from 'react-icons/fa6'
import { IoWarning } from 'react-icons/io5'
import { OrderContext } from '../../context/order.context'
import { infoToast } from '../../utils/firebase/firebase.utils'
import './forms.styles.scss'

const CAKE_TYPES = [
  'Cupcake',
  'Birthday Cake',
  'Anniversary Cake',
  'Celebration Cake',
  'Wedding Cake',
  'Pastries',
  'Custom',
]

const FLAVORS = [
  'Vanilla',
  'Chocolate',
  'Red velvet',
  'Lemon',
  'Strawberry',
  'Custom / other',
]

const TIME_SLOTS = [
  '9:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '1:00 PM',
  '2:00 PM',
  '3:00 PM',
  '4:00 PM',
  '5:00 PM',
  '6:00 PM',
  '7:00 PM',
]

const DRAFT_KEY = 'formFields'
const DRAFT_SAVE_MS = 400

const createDefaultValues = () => ({
  mid: '',
  cake_list: [],
  fullname: '',
  phone: '',
  email: '',
  evt_date: '',
  evt_time: '',
  delivery: 'yes',
  delivery_add: '',
  special_inst: '',
  terms: false,
  status: 'no',
  del: 'no',
  created_at: new Date(),
  updated_at: null,
})

const createDefaultCakeField = () => ({
  c: '',
  cake_type: '',
  qty: '',
  flavor: 'Vanilla',
  specific_dets: '',
  dietary_rests: '',
})

const nextItemId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `cake-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const isFilled = (value) => typeof value === 'string' && value.trim().length > 0

export function Form1({ sendOrderStatus }) {
  const { addOrder } = useContext(OrderContext)

  const [formFields, setFormFields] = useState(createDefaultValues)
  const [cakeField, setCakeField] = useState(createDefaultCakeField)
  const [termsChecked, setTermsChecked] = useState(false)
  const [statusText, setStatusText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [lastRequest, setLastRequest] = useState(null)
  const draftTimer = useRef(null)

  const persistDraft = (next) => {
    if (draftTimer.current) clearTimeout(draftTimer.current)
    draftTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(next))
      } catch (error) {
        console.error('Could not save draft order:', error)
      }
    }, DRAFT_SAVE_MS)
  }

  const updateForm = (updater) => {
    setFormFields((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      persistDraft(next)
      return next
    })
  }

  const changeFieldText = (event) => {
    const { name, value } = event.target
    updateForm((prev) => ({ ...prev, [name]: value }))
  }

  const changeListField = (event) => {
    const { name, value } = event.target
    setCakeField((prev) => ({ ...prev, [name]: value }))
  }

  const setDelivery = (value) => {
    updateForm((prev) => ({
      ...prev,
      delivery: value,
      delivery_add: value === 'no' ? '' : prev.delivery_add,
    }))
  }

  const addToList = () => {
    const qty = Number(cakeField.qty)
    if (!cakeField.cake_type || !Number.isFinite(qty) || qty < 1) {
      return infoToast('Add a cake type and a quantity of 1 or more.')
    }

    const item = {
      ...cakeField,
      c: nextItemId(),
      qty: String(Math.floor(qty)),
      cake_type: cakeField.cake_type.trim(),
      flavor: cakeField.flavor || 'Vanilla',
      specific_dets: cakeField.specific_dets.trim(),
      dietary_rests: cakeField.dietary_rests.trim(),
    }

    updateForm((prev) => ({ ...prev, cake_list: [...prev.cake_list, item] }))
    setCakeField({
      ...createDefaultCakeField(),
      flavor: cakeField.flavor || 'Vanilla',
    })
  }

  const removeFromList = (item) => {
    updateForm((prev) => ({
      ...prev,
      cake_list: prev.cake_list.filter((el) => el.c !== item.c),
    }))
  }

  const submitPreOrder = async (event) => {
    event.preventDefault()

    if (!isFilled(formFields.fullname) || !isFilled(formFields.phone) || !isFilled(formFields.email)) {
      setStatusText('x')
      return infoToast('Please fill in your name, phone, and email.')
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formFields.email.trim())
    if (!emailOk) {
      setStatusText('x')
      return infoToast('Please enter a valid email address.')
    }

    if (formFields.cake_list.length === 0) {
      setStatusText('x')
      return infoToast('Add at least one cake to your list before submitting.')
    }

    if (!termsChecked) {
      setStatusText('x')
      return infoToast('Please read and accept the Terms and Conditions.')
    }

    if (!formFields.evt_date || !formFields.evt_time) {
      setStatusText('x')
      return infoToast('Event date and preferred time are required.')
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(formFields.evt_date)) {
      const [y, m, d] = formFields.evt_date.split('-').map(Number)
      const eventDay = new Date(y, m - 1, d)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (eventDay < today) {
        setStatusText('x')
        return infoToast('Please choose an event date that is today or later.')
      }
    }

    if (formFields.delivery === 'yes' && !isFilled(formFields.delivery_add)) {
      setStatusText('x')
      return infoToast('Please add a delivery address, or switch to pick up.')
    }

    setSubmitting(true)

    const requestId = `CS${Date.now().toString().slice(-8)}`
    const payload = {
      ...formFields,
      fullname: formFields.fullname.trim(),
      phone: formFields.phone.trim(),
      email: formFields.email.trim(),
      delivery_add: formFields.delivery === 'yes' ? formFields.delivery_add.trim() : '',
      special_inst: formFields.special_inst.trim(),
      terms: true,
      mid: requestId,
      created_at: new Date(),
      updated_at: null,
    }

    try {
      await addOrder(payload)
      if (draftTimer.current) clearTimeout(draftTimer.current)
      localStorage.removeItem(DRAFT_KEY)
      setLastRequest({
        mid: requestId,
        email: payload.email,
        evt_date: payload.evt_date,
        evt_time: payload.evt_time,
        delivery: payload.delivery,
      })
      setFormFields(createDefaultValues())
      setCakeField(createDefaultCakeField())
      setTermsChecked(false)
      setStatusText('y')
      if (typeof sendOrderStatus === 'function') sendOrderStatus(2)
    } catch (error) {
      console.error('Order submit failed:', error)
      setStatusText('x')
      infoToast('Could not send your order. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const startAnotherRequest = () => {
    setStatusText('')
    setLastRequest(null)
    if (typeof sendOrderStatus === 'function') sendOrderStatus(0)
  }

  useEffect(() => {
    const localStore = localStorage.getItem(DRAFT_KEY)
    if (!localStore) return undefined

    try {
      const parsed = JSON.parse(localStore)
      setFormFields((prev) => ({
        ...prev,
        ...parsed,
        cake_list: Array.isArray(parsed.cake_list) ? parsed.cake_list : [],
        created_at: new Date(),
        updated_at: null,
      }))
    } catch (error) {
      console.error('Could not restore draft order:', error)
      localStorage.removeItem(DRAFT_KEY)
    }

    return () => {
      if (draftTimer.current) clearTimeout(draftTimer.current)
    }
  }, [])

  if (statusText === 'y') {
    return (
      <div className="book-form book-form--success">
        <FaRegCircleCheck size={28} aria-hidden="true" />
        <h3>Request received</h3>
        <p>
          Thanks for booking with Cake by Sandy. We&apos;ll review your details and reach out within
          24 hours to confirm. No payment has been taken.
        </p>
        {lastRequest ? (
          <ul className="book-form__success-meta">
            <li>
              <strong>Reference</strong> {lastRequest.mid}
            </li>
            <li>
              <strong>Email</strong> {lastRequest.email}
            </li>
            <li>
              <strong>Event</strong> {lastRequest.evt_date}
              {lastRequest.evt_time ? ` · ${lastRequest.evt_time}` : ''}
            </li>
            <li>
              <strong>Fulfillment</strong>{' '}
              {lastRequest.delivery === 'yes' ? 'Delivery' : 'Pick up'}
            </li>
          </ul>
        ) : null}
        <ol className="book-form__success-next">
          <li>We check the date and cake details.</li>
          <li>We reply by email or phone with confirmation and pricing.</li>
          <li>Payment is arranged later, after we agree on the quote.</li>
        </ol>
        <button type="button" onClick={startAnotherRequest}>
          Place another request
        </button>
      </div>
    )
  }

  return (
    <div className="book-form">
      <div className="book-form__head">
        <p className="book-form__eyebrow">Pre-order</p>
        <h3>Tell us about your bake</h3>
        <p>No payment yet. This sends a request so we can confirm the date and quote.</p>
      </div>

      <form onSubmit={submitPreOrder} className="book-form__body" noValidate>
        {statusText === 'x' ? (
          <p className="book-form__alert" role="alert">
            <IoWarning size={16} aria-hidden="true" />
            Please fix the details below and try again.
          </p>
        ) : null}

        <label className="book-form__field">
          <span>Full name</span>
          <input
            name="fullname"
            value={formFields.fullname}
            onChange={changeFieldText}
            type="text"
            required
            autoComplete="name"
          />
        </label>

        <div className="book-form__row">
          <label className="book-form__field">
            <span>Phone</span>
            <input
              name="phone"
              value={formFields.phone}
              onChange={changeFieldText}
              type="tel"
              required
              autoComplete="tel"
            />
          </label>
          <label className="book-form__field">
            <span>Email</span>
            <input
              name="email"
              value={formFields.email}
              onChange={changeFieldText}
              type="email"
              required
              autoComplete="email"
            />
          </label>
        </div>

        <fieldset className="book-form__block">
          <legend>Order details</legend>

          <label className="book-form__field">
            <span>Cake type</span>
            <select name="cake_type" value={cakeField.cake_type} onChange={changeListField}>
              <option value="">Select type</option>
              {CAKE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>

          <div className="book-form__row">
            <label className="book-form__field">
              <span>Quantity</span>
              <input
                name="qty"
                value={cakeField.qty}
                onChange={changeListField}
                type="number"
                min={1}
                step={1}
                placeholder="1 or more"
              />
            </label>
            <label className="book-form__field">
              <span>Flavor</span>
              <select name="flavor" value={cakeField.flavor} onChange={changeListField}>
                {FLAVORS.map((flavor) => (
                  <option key={flavor} value={flavor}>
                    {flavor}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="book-form__field">
            <span>Specific details</span>
            <textarea
              name="specific_dets"
              value={cakeField.specific_dets}
              onChange={changeListField}
              rows={3}
              placeholder="Colors, toppers, message on cake..."
            />
          </label>

          <label className="book-form__field">
            <span>Dietary notes</span>
            <textarea
              name="dietary_rests"
              value={cakeField.dietary_rests}
              onChange={changeListField}
              rows={2}
              placeholder="Nut-free, gluten-free requests, allergies..."
            />
          </label>

          <button type="button" className="book-form__add" onClick={addToList}>
            <BsPlusCircle size={16} aria-hidden="true" />
            Add to list
          </button>

          <div className="book-form__list">
            <p className="book-form__list-title">
              {formFields.cake_list.length === 0
                ? 'No items in your list yet'
                : `${formFields.cake_list.length} item${
                    formFields.cake_list.length === 1 ? '' : 's'
                  } in your list`}
            </p>

            {formFields.cake_list.map((el, index) => (
              <article
                key={el.c}
                className={`book-form__ticket book-form__ticket--${(index % 3) + 1}`}
              >
                <span className="book-form__ticket-mark" aria-hidden="true">
                  {(el.cake_type || '?').charAt(0)}
                </span>
                <div className="book-form__ticket-body">
                  <p className="book-form__ticket-type">{el.cake_type}</p>
                  <div className="book-form__ticket-meta">
                    <span>Qty {el.qty}</span>
                    <span>{el.flavor}</span>
                  </div>
                  {el.specific_dets ? (
                    <p className="book-form__ticket-note">{el.specific_dets}</p>
                  ) : null}
                  {el.dietary_rests ? (
                    <p className="book-form__ticket-diet">{el.dietary_rests}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="book-form__ticket-remove"
                  aria-label={`Remove ${el.cake_type}`}
                  onClick={() => removeFromList(el)}
                >
                  <FaRegTimesCircle size={18} />
                </button>
              </article>
            ))}
          </div>
        </fieldset>

        <fieldset className="book-form__block">
          <legend>Event details</legend>

          <div className="book-form__row">
            <label className="book-form__field">
              <span>Event date</span>
              <input
                name="evt_date"
                value={formFields.evt_date}
                onChange={changeFieldText}
                type="date"
                required
                min={new Date().toISOString().slice(0, 10)}
              />
            </label>
            <label className="book-form__field">
              <span>Preferred time</span>
              <select
                name="evt_time"
                value={formFields.evt_time}
                onChange={changeFieldText}
                required
              >
                <option value="">Select time</option>
                {TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="book-form__toggle" role="group" aria-label="Delivery or pick up">
            <button
              type="button"
              className={formFields.delivery === 'yes' ? 'is-active' : ''}
              onClick={() => setDelivery('yes')}
            >
              Delivery
            </button>
            <button
              type="button"
              className={formFields.delivery === 'no' ? 'is-active' : ''}
              onClick={() => setDelivery('no')}
            >
              Pick up
            </button>
          </div>

          {formFields.delivery === 'yes' ? (
            <label className="book-form__field">
              <span>Delivery address</span>
              <input
                name="delivery_add"
                value={formFields.delivery_add}
                onChange={changeFieldText}
                type="text"
                required
                autoComplete="street-address"
              />
            </label>
          ) : null}

          <label className="book-form__field">
            <span>Special instructions</span>
            <textarea
              name="special_inst"
              value={formFields.special_inst}
              onChange={changeFieldText}
              rows={3}
            />
          </label>
        </fieldset>

        <label className="book-form__terms">
          <input
            type="checkbox"
            checked={termsChecked}
            onChange={(event) => setTermsChecked(event.target.checked)}
          />
          <span>
            I agree to the <Link to="/terms">Terms and Conditions</Link> and have read the{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </span>
        </label>

        <button type="submit" className="book-form__submit" disabled={submitting}>
          {submitting ? 'Sending request…' : 'Send booking request'}
        </button>

        <p className="book-form__note">
          Submitting does not charge your card. We confirm pricing and availability after we review
          your request.
        </p>
      </form>
    </div>
  )
}
